import { FC } from 'react'

import { OPS_BUTTON_HEIGHT_CLASS } from './opsUiShared'

const SkeletonBar: FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse rounded bg-gray-100 ${className}`} />
)

const GroupDetailContentSkeleton: FC = () => (
  <div className="flex flex-col gap-6 w-full min-h-0 flex-1 lg:h-full">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 shrink-0">
      {Array.from({ length: 5 }).map((_, index) => (
        <div
          key={index}
          className="bg-white rounded-lg border border-gray-100 p-4 flex items-center gap-4"
        >
          <SkeletonBar className="w-[50px] h-[50px] rounded-lg shrink-0" />
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <SkeletonBar className="h-7 w-10" />
            <SkeletonBar className="h-4 w-24" />
          </div>
        </div>
      ))}
    </div>

    <div className="flex flex-col gap-4 w-full min-h-0 flex-1">
      <div className="flex gap-6 border-b border-gray-200 shrink-0 pb-2">
        <SkeletonBar className="h-9 w-36" />
        <SkeletonBar className="h-9 w-36" />
      </div>

      <SkeletonBar className="h-10 w-full md:w-1/2 lg:w-[30%] shrink-0 rounded-[6px]" />

      <div className="flex flex-col w-full min-h-[280px] lg:flex-1 lg:min-h-0 rounded-lg border border-gray-100 bg-white overflow-hidden">
        <SkeletonBar className="h-11 w-full rounded-none shrink-0" />
        <div className="flex flex-col gap-3 p-4 flex-1">
          {Array.from({ length: 5 }).map((_, index) => (
            <SkeletonBar key={index} className="h-10 w-full" />
          ))}
        </div>
        <div className="flex justify-end gap-3 p-3 border-t border-gray-100 shrink-0">
          <SkeletonBar className="h-8 w-24" />
          <SkeletonBar className="h-8 w-32" />
        </div>
      </div>
    </div>
  </div>
)

type OpsGroupCardSkeletonProps = {
  name: string
  description?: string
}

const OpsGroupCardSkeleton: FC<OpsGroupCardSkeletonProps> = ({ name, description }) => {
  return (
    <div className="h-full bg-white p-3 border border-gray-100 border-l-4 border-l-gray-100 flex flex-col gap-3 rounded-[4px] select-none">
      <div className="flex justify-between">
        <span className="font-medium text-gray-700 truncate flex-1 min-w-0 pr-3" title={name}>
          {name}
        </span>
        <SkeletonBar className="h-[22px] w-[22px] shrink-0 rounded-sm" />
      </div>

      <div className="flex flex-wrap items-center gap-6">
        <SkeletonBar className="h-4 w-20" />
        <SkeletonBar className="h-4 w-20" />
        <SkeletonBar className="h-4 w-24" />
      </div>

      <div className="flex flex-col gap-1.5">
        <SkeletonBar className="h-2.5 w-[72px]" />
        <div className="flex gap-2 flex-wrap">
          <SkeletonBar className="h-7 w-28 rounded-2xl" />
          <SkeletonBar className="h-7 w-24 rounded-2xl" />
        </div>
      </div>

      {description ? (
        <p className="text-gray-400 text-xs min-h-12 line-clamp-3">{description}</p>
      ) : (
        <SkeletonBar className="h-12 w-full" />
      )}

      <div className="flex flex-wrap gap-2">
        <SkeletonBar className={`${OPS_BUTTON_HEIGHT_CLASS} w-[148px] max-w-[200px] rounded-[4px]`} />
        <SkeletonBar className={`${OPS_BUTTON_HEIGHT_CLASS} w-[200px] max-w-[200px] rounded-[4px]`} />
      </div>

      <div className="flex items-center justify-between mt-auto">
        <SkeletonBar className="h-3 w-40" />
        <div className="flex items-center gap-1">
          <SkeletonBar className="h-5 w-5 rounded-sm" />
          <SkeletonBar className="h-5 w-5 rounded-sm" />
        </div>
      </div>
    </div>
  )
}

export { GroupDetailContentSkeleton, OpsGroupCardSkeleton }
